<script setup lang="ts">
import {
  UIUpload,
  UITextSmRegular,
  UITextXsRegular,
  UIProgress,
  UITextSmMedium,
  UIButton,
} from '@gohighlevel/ghl-ui'
import { Trash01Icon, CheckDone02Icon } from '@gohighlevel/ghl-icons/24/outline'
import { bytes } from '../../../utils'
import { CustomModal } from '../CustomModal'
import { ModalEvents } from '../../../types/components'
import { DraggableList } from '../../DraggableList'

import { computed, ref, watch } from 'vue'
import { useVueI18n } from '../../../locales'
import { DragHandleSVG } from '../../../assets'
import { IDocumentAttachment } from '../../../types'

const t = useVueI18n()
const emits = defineEmits([ModalEvents.CLOSE_MODAL, ModalEvents.SHOW_MODAL])
const fileList = ref<
  {
    id: string
    name: string
    percentage?: number | null
    status: 'pending' | 'uploading' | 'finished' | 'removed' | 'error'
    file?: File | null
    type?: string | null
    order: number
    isValid?: boolean
  }[]
>([])

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  documentId: {
    type: String,
    required: true,
  },
  forTemplate: {
    type: Boolean,
    default: false,
  },
  useAttachments: {
    type: Function,
    required: true,
  },
  allowedFileTypes: {
    type: Array as () => string[],
    required: true,
  },
  currentAttachments: {
    type: Array as () => IDocumentAttachment[],
    required: true,
  },
})

const {
  uploadInBatches,
  isUploading,
  uploadPercentage,
  errorMessage,
  resetErrorMessage,
  cancelUpload,
  validateFile,
  validateTotalFiles,
  validateTotalSize,
  getFileTypeIcon,
} = props.useAttachments()

const isCancelled = ref(false)
const draggable = computed(() => !(isUploading.value || !notHasError.value))

const reorderArray = ({
  oldIndex,
  newIndex,
}: {
  oldIndex: number
  newIndex: number
}) => {
  const newArray = [...(fileList?.value || [])]
  const movedItem = newArray.splice(oldIndex, 1)[0]
  newArray.splice(newIndex, 0, movedItem)

  // Update the order property
  if (oldIndex < newIndex) {
    for (let i = oldIndex; i <= newIndex; i++) {
      newArray[i].order = i + 1
    }
  } else {
    for (let i = newIndex; i <= oldIndex; i++) {
      newArray[i].order = i + 1
    }
  }
  fileList.value = newArray
  return newArray
}

const onSuccess = async () => {
  isCancelled.value = false
  try {
    isUploading.value = true
    await uploadInBatches(
      fileList.value.map(file => ({
        file: file.file as File,
        documentId: props.documentId,
      }))
    )
    onCancel()
  } catch (error) {
    console.error('Error uploading attachments:', error)
  }
}

const onCancel = async () => {
  isCancelled.value = true
  await resetErrorMessage()
  await cancelUpload()
  fileList.value = []
  emits(ModalEvents.CLOSE_MODAL, false)
}

const notHasError = ref(true)

const hasInvalidFiles = computed(() => {
  return fileList.value.some(file => file.isValid === false)
})

watch(hasInvalidFiles, hasInvalid => {
  if (!hasInvalid) {
    resetErrorMessage()
  }
})

const onRemove = (i: number) => {
  const updatedFileList = [...fileList.value]
  updatedFileList.splice(i, 1)

  // If we're removing the last file or if no invalid files remain after removal
  if (
    updatedFileList.length === 0 ||
    !updatedFileList.some(file => file.isValid === false)
  ) {
    resetErrorMessage()
  }

  onFileListUpdate(updatedFileList)
}

const loadingText = computed(() => {
  if (isUploading.value) {
    if (uploadPercentage.value === 100) {
      return t('proposalsList.loading.uploadComplete')
    }
    return `${t('proposalsList.loading.uploading')} (${
      uploadPercentage.value
    }%)`
  }
  return t('proposalsList.loading.filePrepare')
})

const hasValidFiles = computed(() => {
  // Disable if there are no files or if there's an error message
  return fileList.value.length > 0 && !errorMessage.value
})

const onFileListUpdate = (updatedList: any) => {
  // Reset error state
  resetErrorMessage()

  const newFileList = updatedList.map((file: any, index: number) => ({
    ...file,
    order: index + 1,
    status: 'pending',
    isValid: true, // Default to valid
  }))

  // Validate file count
  const filesValidation = validateTotalFiles(
    updatedList.map((file: any) => file.file)
  )
  if (!filesValidation.valid && filesValidation.error) {
    errorMessage.value = filesValidation.error
    // Mark files beyond limit as invalid
    const validCount = 10 - (props.currentAttachments.length ?? 0)
    newFileList.forEach((file: any, index: number) => {
      if (index >= validCount) {
        file.isValid = false
      }
    })
  }

  // Validate total size
  const totalSizeValidation = validateTotalSize(
    updatedList.map((file: any) => file.file)
  )
  if (!totalSizeValidation.valid && totalSizeValidation.error) {
    errorMessage.value = totalSizeValidation.error
    // Mark files that cause size overflow as invalid
    let currentSize = 0
    newFileList.forEach((file: any) => {
      if (currentSize + (file.file?.size || 0) <= 15000000) {
        // MAX_TOTAL_SIZE constant
        currentSize += file.file?.size || 0
        file.isValid = true
      } else {
        file.isValid = false
      }
    })
  }

  // Validate individual files
  newFileList.forEach((file: any) => {
    if (file.file) {
      const fileValidation = validateFile(file.file)
      if (!fileValidation.valid && fileValidation.error) {
        errorMessage.value = fileValidation.error
        file.isValid = false
      }
    }
  })

  fileList.value = newFileList
}
</script>

<template>
  <CustomModal
    :headerIcon="false"
    :title="t('common.addAttachments')"
    :description="t('common.addAttachmentsDesc')"
    :modalType="'primary'"
    :show="show"
    :successActionText="t('common.upload')"
    :cancelActionText="isUploading ? '' : t('common.cancel')"
    @on-success="onSuccess"
    @on-cancel="onCancel"
    :close-on-success="false"
    :loading="isUploading"
    :disabled="isUploading || !hasValidFiles"
  >
    <div class="relative">
      <UITextXsRegular class="text-red-500 mb-1">{{
        errorMessage
      }}</UITextXsRegular>
      <div
        :style="{
          backgroundColor: 'rgba(255,255,255,1)',
        }"
        class="flex items-center justify-center w-full h-full absolute z-10"
        v-if="isUploading"
      >
        <div class="inline-flex flex-col items-center w-full">
          <div class="w-full text-center">
            <UIProgress
              type="line"
              :percentage="uploadPercentage || 0"
              indicator-placement="inside"
              :processing="true"
            />
          </div>
          <UITextXsRegular>{{ loadingText }}</UITextXsRegular>
        </div>
      </div>
      <UIUpload
        :multiple="true"
        :file-list="fileList"
        @update:file-list="onFileListUpdate"
        :disabled="isUploading"
        :accept="allowedFileTypes.join(',')"
      >
        <template #extra>
          <UITextSmRegular>
            {{ t('common.dragFileUpload') }}
          </UITextSmRegular>
          <UITextXsRegular>{{ t('common.supportedFormats') }}</UITextXsRegular>
          <UITextXsRegular>{{ t('common.maxSize15MB') }}</UITextXsRegular>
        </template>
      </UIUpload>
      <DraggableList
        :list="fileList"
        @on-update="reorderArray"
        :draggable="draggable"
        class="px-2 file-list max-h-[20em] overflow-auto border-b"
      >
        <template #listItem="slotProps">
          <div
            class="bg-white file-box"
            :class="{
              'invalid-file': slotProps.data.isValid === false,
            }"
          >
            <div class="w-full flex items-center">
              <div class="w-10/12 flex items-center rounded-sm my-2">
                <div class="handle w-1/12 flex">
                  <DragHandleSVG class="remove-selecto-selection" />
                </div>
                <div class="flex items-center flex-1 max-w-full w-11/12">
                  <div class="w-2/12">
                    <span
                      class="h-10 w-10 rounded-full bg-gray-100 flex justify-center items-center mr-2"
                    >
                      <component
                        :is="getFileTypeIcon(slotProps.data.type)"
                        class="w-5 h-5 text-gray-500 shrink-0"
                      />
                    </span>
                  </div>
                  <div class="w-10/12 flex flex-col flex-1">
                    <UITextSmMedium class="ellipsis">{{
                      slotProps.data.name
                    }}</UITextSmMedium>
                    <UITextXsRegular>{{
                      bytes.format(slotProps.data.file?.size || 0)
                    }}</UITextXsRegular>
                  </div>
                </div>
              </div>

              <div class="w-2/12 flex justify-end items-center">
                <UIProgress
                  v-if="isUploading"
                  :percentage="slotProps.data.percentage || 0"
                  type="circle"
                  :processing="true"
                  :showIndicator="false"
                  :circleGap="0"
                  :gapDegree="0"
                  :height="2"
                  :offsetDegree="0"
                  :strokeWidth="12"
                  unit="%"
                >
                  <template #default>
                    <CheckDone02Icon class="w-4 h-4 text-success-500" />
                  </template>
                </UIProgress>
                <UIButton
                  v-if="!isUploading"
                  :id="`delete-file-${slotProps.index}`"
                  type="default"
                  size="medium"
                  :quaternary="true"
                  :circle="true"
                  @click="() => onRemove(slotProps.index)"
                >
                  <Trash01Icon
                    class="w-4 h-4"
                    :class="{
                      'text-error-700': slotProps.data.isValid === false,
                    }"
                  />
                </UIButton>
              </div>
            </div>
          </div>
        </template>
      </DraggableList>
    </div>
  </CustomModal>
</template>

<style lang="scss">
.file-list {
  padding: 0 !important;
  .list-group-item {
    margin: 1em 0px;
    padding: 0em 0px;
    border: none !important;
  }
  .n-progress.n-progress--circle.n-progress--default {
    width: 30px;
  }
  .file-box {
    border-radius: 12px;
    padding: 8px 12px;
  }
  .invalid-file {
    position: relative;
    background-color: var(--error-25) !important;
    color: var(--error-700);
    border: 1px solid var(--error-300);
  }
}
.n-upload-trigger {
  .upload-icon {
    width: 32px;
    height: 32px;
    padding: 4px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
}
.n-upload-file-list {
  display: none;
}
.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
